import React, { useEffect, useState } from 'react';
import {
  Typography,
  Stack,
  Box,
  Alert,
  LinearProgress,
} from '@mui/material';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import { reportApi, PlatformStats } from '../../services/reportService';
import { PageHeader, SectionCard, StatTile, EmptyState, LoadingState, BookingStatusPill, BOOKING_STATUS, toneColors } from '../../components/ui';
import { tokens } from '../../theme';
import { countLabel, formatMoney, formatNumber, formatPercent } from '../../utils/format';

const STATUS_ORDER = ['COMPLETED', 'CONFIRMED', 'IN_PROGRESS', 'PENDING', 'CANCELLED', 'NO_SHOW'];

export const SuperAdminStatsPage: React.FC = () => {
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const data = await reportApi.getPlatformStats();
        setStats(data);
      } catch {
        setError('Nie udało się załadować statystyk platformy. Odśwież stronę za chwilę.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const header = (
    <PageHeader
      breadcrumbs={[{ label: 'Administracja' }]}
      title="Statystyki platformy"
      description="Salony, klienci, wizyty i przychód w całym Visitio."
    />
  );

  if (loading) {
    return (
      <Box>
        {header}
        <LoadingState variant="page" count={4} rows={4} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        {header}
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const totalSalons = stats?.totalSalons || 0;
  const activeSalons = stats?.activeSalons || 0;

  const statusEntries = (stats?.bookingsByStatus ? Object.entries(stats.bookingsByStatus) : [])
    .sort(([a], [b]) => {
      const ia = STATUS_ORDER.indexOf(a);
      const ib = STATUS_ORDER.indexOf(b);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });
  const totalByStatus = statusEntries.reduce((sum, [, count]) => sum + count, 0) || 1;

  return (
    <Box>
      {header}

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mb: 2.5,
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
        }}
      >
        <StatTile
          tone="hero"
          label="Łączny przychód"
          value={formatMoney(stats?.totalRevenue || 0, { decimals: 0 })}
          icon={<PaymentsOutlinedIcon />}
          hint="Wszystkie salony od początku działania"
        />
        <StatTile
          label="Salony na platformie"
          value={formatNumber(totalSalons)}
          icon={<StorefrontOutlinedIcon />}
          progress={totalSalons ? (activeSalons / totalSalons) * 100 : undefined}
          hint={`W tym ${countLabel(activeSalons, 'aktywny', 'aktywne', 'aktywnych')}`}
        />
        <StatTile
          label="Wizyty łącznie"
          value={formatNumber(stats?.totalBookings || 0)}
          icon={<CalendarMonthOutlinedIcon />}
          hint="Wszystkie rezerwacje w systemie"
        />
        <StatTile
          label="Zarejestrowani klienci"
          value={formatNumber(stats?.totalClients || 0)}
          icon={<PeopleAltOutlinedIcon />}
          hint="Konta klientów w Visitio"
        />
      </Box>

      <SectionCard
        title="Wizyty według statusu"
        subtitle={statusEntries.length ? `Podział ${countLabel(totalByStatus, 'wizyty', 'wizyt', 'wizyt')} na platformie` : undefined}
        icon={<QueryStatsOutlinedIcon />}
        sx={{ maxWidth: 880 }}
      >
        {statusEntries.length === 0 ? (
          <EmptyState compact title="Brak wizyt do podsumowania" description="Podział pojawi się, gdy klienci zarezerwują pierwsze wizyty." />
        ) : (
          <Stack gap={1.75}>
            {statusEntries.map(([status, count]) => {
              const tone = BOOKING_STATUS[status]?.tone || 'neutral';
              const pct = (count / totalByStatus) * 100;
              return (
                <Box
                  key={status}
                  sx={{
                    display: 'grid',
                    alignItems: 'center',
                    columnGap: 2,
                    rowGap: 0.75,
                    gridTemplateColumns: { xs: 'minmax(0, 1fr) auto', sm: '170px minmax(0, 1fr) 64px 52px' },
                  }}
                >
                  <Box><BookingStatusPill status={status} /></Box>
                  <LinearProgress
                    variant="determinate"
                    value={pct}
                    aria-label={`${BOOKING_STATUS[status]?.label || status}: ${formatPercent(pct)}`}
                    sx={{
                      gridColumn: { xs: '1 / -1', sm: 'auto' },
                      gridRow: { xs: 2, sm: 'auto' },
                      height: 8,
                      '& .MuiLinearProgress-bar': { bgcolor: toneColors(tone).dot },
                    }}
                  />
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {formatNumber(count)}
                  </Typography>
                  <Typography
                    sx={{
                      display: { xs: 'none', sm: 'block' },
                      fontSize: '0.875rem',
                      color: tokens.muted,
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {formatPercent(pct)}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        )}
      </SectionCard>
    </Box>
  );
};
